export const DEFAULT_3D_PITCH = 60;
export const DEFAULT_ROTATION_STEP_DEGREES = 30;
export const MAX_CAMERA_PITCH = 85;
export const MIN_CAMERA_PITCH = 0;
export const TRACKPAD_PITCH_SENSITIVITY = 0.08;
export const TRACKPAD_ROTATION_SENSITIVITY = 0.12;
export const TRACKPAD_ZOOM_SENSITIVITY = 0.01;

const THREE_D_PITCH_THRESHOLD = 5;

/** Normalizes map bearing into the maplibre range of [-180, 180). */
export function normalizeBearing(bearing: number): number {
  const wrapped = ((bearing + 180) % 360 + 360) % 360;
  return wrapped - 180;
}

/** Returns the shortest signed angular delta from startAngle to endAngle in [-180, 180). */
export function getShortestSignedAngleDelta(startAngle: number, endAngle: number): number {
  const rawDelta = endAngle - startAngle;
  return ((rawDelta + 540) % 360) - 180;
}

export function smoothBearingToward(
  currentBearing: number,
  targetBearing: number,
  maxStepDegrees: number,
): number {
  const normalizedCurrent = normalizeBearing(Number.isFinite(currentBearing) ? currentBearing : 0);
  const normalizedTarget = normalizeBearing(Number.isFinite(targetBearing) ? targetBearing : normalizedCurrent);
  if (!Number.isFinite(maxStepDegrees) || maxStepDegrees <= 0) {
    return normalizedTarget;
  }

  const delta = getShortestSignedAngleDelta(normalizedCurrent, normalizedTarget);
  const step = Math.min(Math.abs(delta), maxStepDegrees);
  if (step <= 1e-9) return normalizedCurrent;
  return normalizeBearing(normalizedCurrent + (Math.sign(delta) * step));
}

export function smoothBearingTowardDamped(
  currentBearing: number,
  targetBearing: number,
  elapsedMs: number,
  options?: {
    responseMs?: number;
    maxTurnRateDegPerSecond?: number;
  },
): number {
  const normalizedCurrent = normalizeBearing(Number.isFinite(currentBearing) ? currentBearing : 0);
  const normalizedTarget = normalizeBearing(Number.isFinite(targetBearing) ? targetBearing : normalizedCurrent);
  const safeElapsedMs = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  if (safeElapsedMs <= 0) return normalizedCurrent;

  const responseMs = Number.isFinite(options?.responseMs) && (options?.responseMs ?? 0) > 0
    ? options?.responseMs as number
    : 280;
  const alpha = 1 - Math.exp(-safeElapsedMs / responseMs);
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  const delta = getShortestSignedAngleDelta(normalizedCurrent, normalizedTarget);
  const dampedTarget = normalizeBearing(normalizedCurrent + (delta * clampedAlpha));

  const maxTurnRateDegPerSecond = options?.maxTurnRateDegPerSecond;
  if (Number.isFinite(maxTurnRateDegPerSecond) && (maxTurnRateDegPerSecond ?? 0) > 0) {
    const maxStepDegrees = (maxTurnRateDegPerSecond as number) * (safeElapsedMs / 1000);
    return smoothBearingToward(normalizedCurrent, dampedTarget, maxStepDegrees);
  }

  return dampedTarget;
}

export function is3DEnabled(pitch: number): boolean {
  return pitch > THREE_D_PITCH_THRESHOLD;
}

export function getNextPitchFor3DToggle(
  currentPitch: number,
  threeDPitch: number = DEFAULT_3D_PITCH,
): number {
  return is3DEnabled(currentPitch) ? MIN_CAMERA_PITCH : clampPitch(threeDPitch);
}

export function rotateBearing(currentBearing: number, stepDegrees: number): number {
  return normalizeBearing(currentBearing + stepDegrees);
}

export function clampPitch(pitch: number, min: number = MIN_CAMERA_PITCH, max: number = MAX_CAMERA_PITCH): number {
  if (max < min) return min;
  return Math.min(Math.max(pitch, min), max);
}

export function getNextPitchFromWheelDelta(
  currentPitch: number,
  deltaY: number,
  sensitivity: number = TRACKPAD_PITCH_SENSITIVITY,
): number {
  return clampPitch(currentPitch - deltaY * sensitivity);
}

export function getNextBearingFromWheelDelta(
  currentBearing: number,
  deltaX: number,
  sensitivity: number = TRACKPAD_ROTATION_SENSITIVITY,
): number {
  return rotateBearing(currentBearing, deltaX * sensitivity);
}

export function getNextZoomFromWheelDelta(
  currentZoom: number,
  deltaY: number,
  minZoom: number,
  maxZoom: number,
  sensitivity: number = TRACKPAD_ZOOM_SENSITIVITY,
): number {
  if (maxZoom < minZoom) return minZoom;
  const nextZoom = currentZoom - deltaY * sensitivity;
  return Math.min(Math.max(nextZoom, minZoom), maxZoom);
}
